import { useId, useState } from 'react';
import {
  calculatorDefinitions,
  calculateTool,
  buildUtmUrl
} from '../../utils/shopifyToolMath';
import { getGuideToolUi } from '../../content/shopify/guideLocale';

const inputClass =
  'mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200';
const buttonClass =
  'rounded-xl bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';
function Field({
  label,
  value,
  onChange,
  type = 'text',
  percent = false,
  integer = false,
  multiline = false
}) {
  const id = useId();
  const props = {
    id,
    value,
    onChange: (e) => onChange(e.target.value),
    className: inputClass
  };
  return (
    <label htmlFor={id} className="block text-sm font-semibold text-slate-700">
      {label}
      {multiline ? (
        <textarea {...props} rows={4} />
      ) : (
        <input
          {...props}
          type={type}
          {...(type === 'number'
            ? {
                min: 0,
                max: percent ? 100 : undefined,
                step: integer ? 1 : 'any',
                inputMode: integer ? 'numeric' : 'decimal'
              }
            : {})}
        />
      )}
    </label>
  );
}
const interpolate = (text, values) =>
  text.replace(/\{(\w+)\}/g, (_, key) => values[key]);

function ExportText({ value, filename, locale }) {
  const ui = getGuideToolUi(locale);
  const [message, setMessage] = useState('');
  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error();
      await navigator.clipboard.writeText(value);
      setMessage(ui.copied);
    } catch {
      setMessage(ui.copyUnavailable);
    }
  }
  function download() {
    const url = URL.createObjectURL(
      new Blob([value], { type: 'text/plain;charset=utf-8' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setMessage(ui.downloadPrepared);
  }
  return (
    <div className="mt-4">
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={copy} className={buttonClass}>
          {ui.copy}
        </button>
        <button
          type="button"
          onClick={download}
          className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500"
        >
          {ui.download}
        </button>
      </div>
      <p className="mt-2 text-sm text-slate-600" role="status">
        {message}
      </p>
    </div>
  );
}
function Calculator({ type, locale }) {
  const definition = calculatorDefinitions[type];
  const ui = getGuideToolUi(locale);
  const [values, setValues] = useState(() =>
    Object.fromEntries(definition.fields.map((f) => [f.key, String(f.value)]))
  );
  const result = calculateTool(type, values);
  const format = (value, unit) =>
    typeof value === 'string'
      ? value
      : unit === 'percent'
      ? `${new Intl.NumberFormat(
          { en: 'en-US', pl: 'pl-PL', uk: 'uk-UA' }[locale] || 'en-US',
          { maximumFractionDigits: 2 }
        ).format(value)}%`
      : unit === 'count'
      ? new Intl.NumberFormat(
          { en: 'en-US', pl: 'pl-PL', uk: 'uk-UA' }[locale] || 'en-US',
          { maximumFractionDigits: 2 }
        ).format(value)
      : new Intl.NumberFormat(
          { en: 'en-US', pl: 'pl-PL', uk: 'uk-UA' }[locale] || 'en-US',
          {
            style: 'currency',
            currency: 'USD'
          }
        ).format(value);
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        {definition.fields.map((f) => (
          <Field
            key={f.key}
            label={ui.fields[f.key] || f.label}
            type="number"
            percent={f.percent}
            integer={f.integer}
            value={values[f.key]}
            onChange={(value) =>
              setValues((current) => ({ ...current, [f.key]: value }))
            }
          />
        ))}
      </div>
      <div
        className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-5"
        aria-live="polite"
        aria-atomic="true"
      >
        {result.error ? (
          <p className="text-red-800">{ui.calculationError}</p>
        ) : (
          <dl className="grid gap-5 sm:grid-cols-2">
            {result.results.map(([label, value, unit]) => (
              <div key={label}>
                <dt className="text-sm text-slate-700">{ui.result}</dt>
                <dd className="mt-1 text-2xl font-bold tracking-tight text-blue-900">
                  {format(value, unit)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        <strong>{ui.estimate}</strong> {ui.estimateNote}
      </p>
    </>
  );
}
function Checklist({ tool, locale }) {
  const ui = getGuideToolUi(locale);
  const items =
    locale === 'en' && tool.items?.length ? tool.items : ui.checklist.items;
  const [checked, setChecked] = useState([]);
  return (
    <>
      <p className="font-semibold text-blue-900" role="status">
        {interpolate(ui.checklist.complete, {
          complete: checked.length,
          total: items.length
        })}
      </p>
      <progress
        className="my-4 h-3 w-full accent-blue-700"
        max={items.length}
        value={checked.length}
        aria-label={ui.checklist.progress}
      />
      <div className="space-y-3">
        {items.map((item, i) => (
          <label
            key={i}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
          >
            <input
              type="checkbox"
              checked={checked.includes(i)}
              onChange={() =>
                setChecked((current) =>
                  current.includes(i)
                    ? current.filter((n) => n !== i)
                    : [...current, i]
                )
              }
              className="mt-1 h-5 w-5 shrink-0 accent-blue-700"
            />
            <span className="text-slate-700">{item}</span>
          </label>
        ))}
      </div>
      <button
        className="mt-4 rounded-lg px-3 py-2 font-semibold text-blue-700 underline focus:ring-2 focus:ring-blue-500"
        type="button"
        onClick={() => setChecked([])}
      >
        {ui.checklist.reset}
      </button>
      <p className="mt-2 text-sm text-slate-500">{ui.checklist.session}</p>
    </>
  );
}
function Utm({ locale }) {
  const ui = getGuideToolUi(locale);
  const [values, setValues] = useState({
    url: ui.utm.url,
    source: ui.utm.source,
    medium: ui.utm.medium,
    campaign: ui.utm.campaign
  });
  const result = buildUtmUrl(
    values.url,
    values.source,
    values.medium,
    values.campaign
  );
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        {['url', 'source', 'medium', 'campaign'].map((key) => (
          <Field
            key={key}
            label={ui.fields[key]}
            value={values[key]}
            onChange={(value) =>
              setValues((current) => ({ ...current, [key]: value }))
            }
          />
        ))}
      </div>
      <div className="mt-5" aria-live="polite">
        {result.error ? (
          <p className="text-red-800">{ui.calculationError}</p>
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-700">
              {ui.utm.heading}
            </p>
            <pre className="mt-2 whitespace-pre-wrap break-all rounded-xl bg-blue-50 p-4 text-sm text-blue-900">
              {result.url}
            </pre>
            <ExportText
              value={result.url}
              filename="campaign-url.txt"
              locale={locale}
            />
          </>
        )}
      </div>
      <p className="mt-4 text-sm text-slate-600">{ui.utm.note}</p>
    </>
  );
}
function Template({ tool, brief = false, locale }) {
  const ui = getGuideToolUi(locale);
  const [values, setValues] = useState({
    brand: ui.template.brand,
    partner: ui.template.partner,
    product: ui.template.product,
    objective: ui.template.objective,
    deliverables: ui.template.deliverables,
    timeline: ui.template.timeline,
    message: ui.template.message,
    terms: ui.template.terms,
    disclosure: ui.template.disclosure
  });
  const fields = brief
    ? [
        ['brand', ui.template.brandLabel],
        ['partner', ui.template.creatorLabel],
        ['product', ui.template.productLabel],
        ['objective', ui.template.objectiveLabel],
        ['deliverables', ui.template.deliverablesLabel],
        ['timeline', ui.template.timelineLabel],
        ['message', ui.template.messageLabel],
        ['terms', ui.template.termsLabel],
        ['disclosure', ui.template.disclosureLabel]
      ]
    : [
        ['brand', ui.template.brandLabel],
        ['partner', ui.template.partnerLabel],
        ['product', ui.template.productLabel]
      ];
  const source =
    locale === 'en' && tool.template ? tool.template : ui.template.messageBody;
  const value = brief
    ? `${ui.template.briefTitle}\n\n${fields
        .map(([key, label]) => `${label}: ${values[key]}`)
        .join('\n\n')}`
    : source.replace(/\{(brand|partner|product)\}/g, (_, key) => values[key]);
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map(([key, label]) => (
          <Field
            key={key}
            label={label}
            value={values[key]}
            multiline={brief && !['brand', 'partner', 'product'].includes(key)}
            onChange={(next) =>
              setValues((current) => ({ ...current, [key]: next }))
            }
          />
        ))}
      </div>
      <p className="mt-5 text-sm font-semibold text-slate-700">
        {brief ? ui.template.briefHeading : ui.template.messageHeading}
      </p>
      <pre className="mt-2 whitespace-pre-wrap break-words rounded-xl bg-blue-50 p-5 font-sans text-sm leading-7 text-slate-800">
        {value}
      </pre>
      <ExportText
        value={value}
        filename={brief ? 'creator-brief.txt' : 'partner-message.txt'}
        locale={locale}
      />
      <p className="mt-3 text-sm text-slate-600">{ui.template.review}</p>
    </>
  );
}
function Quiz({ tool, locale }) {
  const ui = getGuideToolUi(locale);
  const questions =
    locale === 'en' && tool.questions?.length
      ? tool.questions
      : [
          {
            question: ui.quiz.question,
            options: [
              {
                label: ui.quiz.first,
                advice: ui.quiz.firstAdvice
              },
              {
                label: ui.quiz.second,
                advice: ui.quiz.secondAdvice
              }
            ]
          }
        ];
  const [answers, setAnswers] = useState({});
  const groupId = useId();
  return (
    <>
      <div className="space-y-6">
        {questions.map((q, i) => (
          <fieldset key={i}>
            <legend className="mb-3 font-semibold text-slate-900">
              {q.question}
            </legend>
            <div className="space-y-2">
              {q.options.map((option, j) => (
                <label
                  key={j}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                >
                  <input
                    type="radio"
                    name={`${groupId}-${i}`}
                    checked={answers[i] === j}
                    onChange={() =>
                      setAnswers((current) => ({ ...current, [i]: j }))
                    }
                    className="mt-1 h-4 w-4 shrink-0 accent-blue-700"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {answers[i] !== undefined && (
              <p
                role="status"
                className="mt-3 rounded-xl bg-blue-50 p-4 leading-7 text-blue-900"
              >
                {q.options[answers[i]].advice}
              </p>
            )}
          </fieldset>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-600">
        {interpolate(ui.quiz.answered, {
          complete: Object.keys(answers).length,
          total: questions.length
        })}
      </p>
      <button
        type="button"
        onClick={() => setAnswers({})}
        className="mt-3 rounded-lg px-3 py-2 font-semibold text-blue-700 underline focus:ring-2 focus:ring-blue-500"
      >
        {ui.quiz.reset}
      </button>
    </>
  );
}
export default function ShopifyTool({ tool, locale = 'en' }) {
  const headingId = useId();
  if (!tool) return null;
  return (
    <section
      aria-labelledby={headingId}
      className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8"
    >
      <h2
        id={headingId}
        className="text-2xl font-bold tracking-tight text-slate-900"
      >
        {tool.title}
      </h2>
      {tool.description && (
        <p className="mt-3 leading-7 text-slate-600">{tool.description}</p>
      )}
      <div className="mt-6">
        {calculatorDefinitions[tool.type] ? (
          <Calculator
            key={`${locale}-${tool.type}-${tool.title}`}
            type={tool.type}
            locale={locale}
          />
        ) : tool.type === 'utm' ? (
          <Utm locale={locale} />
        ) : tool.type === 'checklist' ? (
          <Checklist
            key={`${locale}-${tool.title}`}
            tool={tool}
            locale={locale}
          />
        ) : tool.type === 'quiz' ? (
          <Quiz key={`${locale}-${tool.title}`} tool={tool} locale={locale} />
        ) : (
          <Template
            key={`${locale}-${tool.title}`}
            tool={tool}
            brief={tool.type === 'brief'}
            locale={locale}
          />
        )}
      </div>
    </section>
  );
}
